import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabase';
import { analyticsApi, whatsappApi } from '../utils/api';
import { 
  ChartBarIcon, 
  ChatBubbleLeftRightIcon, 
  UserGroupIcon, 
  ClockIcon,
  CurrencyDollarIcon,
  ShoppingBagIcon,
  TruckIcon,
  PhoneIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusIcon,
  EyeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { format, subDays, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

// Enregistrement des composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    activeDeliveries: 0,
    whatsappStatus: 'disconnected',
    revenueGrowth: 0,
    ordersGrowth: 0,
    deliveriesGrowth: 0
  });
  
  const [revenueChartData, setRevenueChartData] = useState(null);
  const [ordersChartData, setOrdersChartData] = useState(null);
  const [topProductsData, setTopProductsData] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fonction pour charger toutes les données du dashboard
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Récupération des données agrégées du dashboard
      const dashboardResponse = await analyticsApi.getDashboard();
      const dashboardData = dashboardResponse?.data || {};

      // Statut WhatsApp (non inclus dans le dashboard backend)
      let whatsappStatus = 'disconnected';
      try {
        const statusResponse = await whatsappApi.getStatus();
        whatsappStatus = statusResponse?.data?.status?.toLowerCase() || 'disconnected';
      } catch (error) {
        console.log('WhatsApp API non disponible');
      }

      // Remplissage des stats venant du backend
      if (dashboardData.stats) {
        setStats({
          ...dashboardData.stats,
          whatsappStatus,
        });
      }

      // ========= Graphiques =========
      if (dashboardData.revenueLast7Days) {
        const labels = dashboardData.revenueLast7Days.map(item => {
          return format(parseISO(item.date), 'dd/MM');
        });
        const revenues = dashboardData.revenueLast7Days.map(item => item.total_revenue);

        setRevenueChartData({
          labels,
          datasets: [{
            label: 'Revenus (XOF)',
            data: revenues,
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            tension: 0.4,
            fill: true,
            borderWidth: 3,
            pointBackgroundColor: '#2563eb',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8
          }]
        });
      }

      if (dashboardData.ordersLast7Days) {
        const labels = dashboardData.ordersLast7Days.map(item => {
          return format(parseISO(item.date), 'dd/MM');
        });
        const ordersPerDay = dashboardData.ordersLast7Days.map(item => item.total_orders);

        setOrdersChartData({
          labels,
          datasets: [{
            label: 'Commandes',
            data: ordersPerDay,
            backgroundColor: (context) => {
              const ctx = context.chart.ctx;
              const gradient = ctx.createLinearGradient(0, 0, 0, 400);
              gradient.addColorStop(0, 'rgba(6, 182, 212, 0.8)');
              gradient.addColorStop(1, 'rgba(6, 182, 212, 0.2)');
              return gradient;
            },
            borderColor: '#06b6d4',
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          }]
        });
      }

      // ========= Top produits =========
      if (dashboardData.topProducts) {
        const labels = dashboardData.topProducts.map(p => p.product_name);
        const values = dashboardData.topProducts.map(p => p.total_revenue);

        setTopProductsData({
          labels,
          datasets: [{
            data: values,
            backgroundColor: [
              '#3B82F6', // Bleu vif
              '#10B981', // Vert émeraude
              '#F59E0B', // Orange ambre
              '#EF4444', // Rouge
              '#8B5CF6', // Violet
              '#EC4899', // Rose
              '#14B8A6', // Turquoise
              '#F97316', // Orange foncé
              '#6366F1', // Indigo
              '#06B6D4'  // Cyan
            ],
            borderWidth: 0,
            hoverOffset: 15
          }]
        });
      }

      // ========= Activité récente =========
      if (dashboardData.recentOrders) {
        const activities = dashboardData.recentOrders.map(order => ({
          id: order.id,
          type: 'order',
          title: `Commande #${order.order_number || order.id}`,
          description: `${order.total_amount || 0} XOF`,
          time: order.created_at,
          status: order.status,
        }));
        setRecentActivity(activities);
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Fonction pour formater la monnaie
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Composant de carte de statistique ultra-modernisée
  const StatCard = ({ title, value, icon: Icon, growth, color = 'blue', action }) => {
    // Définir les couleurs disponibles pour éviter les erreurs
    const colorClasses = {
      blue: {
        gradient: 'from-blue-400 via-blue-500 to-blue-600',
        gradientHover: 'hover:from-blue-500 hover:via-blue-600 hover:to-blue-700',
        shadow: 'hover:shadow-blue-500/15',
        icon: 'from-blue-500 to-blue-600',
        iconGlow: 'from-blue-400 to-blue-500',
        button: 'from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 text-blue-700'
      },
      green: {
        gradient: 'from-green-400 via-green-500 to-green-600',
        gradientHover: 'hover:from-green-500 hover:via-green-600 hover:to-green-700',
        shadow: 'hover:shadow-green-500/15',
        icon: 'from-green-500 to-green-600',
        iconGlow: 'from-green-400 to-green-500',
        button: 'from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 text-green-700'
      },
      red: {
        gradient: 'from-red-400 via-red-500 to-red-600',
        gradientHover: 'hover:from-red-500 hover:via-red-600 hover:to-red-700',
        shadow: 'hover:shadow-red-500/15',
        icon: 'from-red-500 to-red-600',
        iconGlow: 'from-red-400 to-red-500',
        button: 'from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 text-red-700'
      }
    };

    const currentColor = colorClasses[color] || colorClasses.blue;

    return (
      <div className={`group relative overflow-hidden rounded-xl p-1 bg-gradient-to-r ${currentColor.gradient} ${currentColor.gradientHover} transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${currentColor.shadow}`}>
        <div className="relative bg-white/95 rounded-lg p-5 h-full backdrop-blur-sm">
          {/* Effet de brillance */}
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:via-white/40 transition-all duration-300"></div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`relative p-3 bg-gradient-to-br ${currentColor.icon} rounded-lg shadow-sm group-hover:shadow-md transition-all duration-300`}>
                <Icon className="h-5 w-5 text-white" aria-hidden="true" />
                {/* Effet de glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${currentColor.iconGlow} rounded-lg opacity-0 group-hover:opacity-10 blur-sm transition-all duration-300`}></div>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</dt>
                <dd className="mt-1 text-xl font-semibold text-gray-900 group-hover:text-gray-800 transition-colors duration-300">{value}</dd>
                {growth && (
                  <div className="mt-1 flex items-center">
                    {growth > 0 ? (
                      <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-3 w-3 text-red-500 mr-1" />
                    )}
                    <span className={`text-xs font-medium ${growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(growth)}%
                    </span>
                    <span className="text-xs text-gray-400 ml-1">vs mois dernier</span>
                  </div>
                )}
              </div>
            </div>
            {action && (
              <button 
                onClick={action.onClick}
                className={`px-3 py-1.5 bg-gradient-to-r ${currentColor.button} rounded-lg text-xs font-medium transition-all duration-300 hover:shadow-sm transform hover:-translate-y-0.5`}
              >
                {action.label}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Composant de raccourci d'action ultra-modernisé
  const QuickAction = ({ title, description, icon: Icon, onClick, color = 'blue' }) => {
    // Définir les couleurs disponibles pour éviter les erreurs
    const colorClasses = {
      blue: {
        background: 'from-white to-blue-50',
        border: 'border-blue-100 hover:border-blue-200',
        shadow: 'hover:shadow-blue-500/5',
        backgroundHover: 'from-blue-500/3 to-blue-600/3',
        icon: 'from-blue-400 to-blue-500',
        titleHover: 'group-hover:text-blue-900',
        arrow: 'text-blue-400'
      },
      green: {
        background: 'from-white to-green-50',
        border: 'border-green-100 hover:border-green-200',
        shadow: 'hover:shadow-green-500/5',
        backgroundHover: 'from-green-500/3 to-green-600/3',
        icon: 'from-green-400 to-green-500',
        titleHover: 'group-hover:text-green-900',
        arrow: 'text-green-400'
      },
      purple: {
        background: 'from-white to-purple-50',
        border: 'border-purple-100 hover:border-purple-200',
        shadow: 'hover:shadow-purple-500/5',
        backgroundHover: 'from-purple-500/3 to-purple-600/3',
        icon: 'from-purple-400 to-purple-500',
        titleHover: 'group-hover:text-purple-900',
        arrow: 'text-purple-400'
      },
      emerald: {
        background: 'from-white to-emerald-50',
        border: 'border-emerald-100 hover:border-emerald-200',
        shadow: 'hover:shadow-emerald-500/5',
        backgroundHover: 'from-emerald-500/3 to-emerald-600/3',
        icon: 'from-emerald-400 to-emerald-500',
        titleHover: 'group-hover:text-emerald-900',
        arrow: 'text-emerald-400'
      }
    };

    const currentColor = colorClasses[color] || colorClasses.blue;

    return (
      <button
        onClick={onClick}
        className={`group relative w-full p-4 bg-gradient-to-br ${currentColor.background} rounded-xl border ${currentColor.border} transition-all duration-300 text-left hover:shadow-md ${currentColor.shadow} hover:-translate-y-1 overflow-hidden`}
      >
        {/* Effet de fond animé */}
        <div className={`absolute inset-0 bg-gradient-to-br ${currentColor.backgroundHover} opacity-0 group-hover:opacity-100 transition-all duration-300`}></div>
        
        <div className="relative flex items-center">
          <div className={`p-2.5 bg-gradient-to-br ${currentColor.icon} rounded-lg shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-300`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div className="ml-3">
            <div className={`font-medium text-gray-900 ${currentColor.titleHover} transition-colors duration-300`}>{title}</div>
            <div className="text-xs text-gray-500 group-hover:text-gray-600 transition-colors duration-300">{description}</div>
          </div>
          {/* Flèche animée */}
          <div className="ml-auto opacity-0 group-hover:opacity-100 transform translate-x-1 group-hover:translate-x-0 transition-all duration-300">
            <ArrowTrendingUpIcon className={`h-4 w-4 ${currentColor.arrow}`} />
          </div>
        </div>
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-2 border-blue-200 mx-auto"></div>
            <div className="animate-ping absolute inset-0 rounded-full h-16 w-16 border-2 border-blue-300 mx-auto"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Chargement du tableau de bord...</p>
          <div className="mt-2 flex justify-center">
            <SparklesIcon className="h-4 w-4 text-blue-400 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/40 to-slate-100/60 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête avec effet de profondeur */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 via-blue-800 to-indigo-800 bg-clip-text text-transparent mb-3">
            Tableau de bord
          </h1>
          <p className="text-base text-gray-600 font-normal">
            Vue d'ensemble de votre activité 
            <span className="bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent font-medium"> Mercuri Shop</span>
          </p>
          <div className="mt-3 w-16 h-0.5 bg-gradient-to-r from-blue-400 to-indigo-400 mx-auto rounded-full"></div>
        </div>

        {/* Métriques principales avec espacement amélioré */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <StatCard
            title="Revenus du mois"
            value={`${formatCurrency(stats.totalRevenue)} XOF`}
            icon={CurrencyDollarIcon}
            growth={stats.revenueGrowth}
            color="green"
          />
          <StatCard
            title="Commandes"
            value={stats.totalOrders}
            icon={ShoppingBagIcon}
            growth={stats.ordersGrowth}
            color="blue"
          />

          <StatCard
            title="WhatsApp"
            value={stats.whatsappStatus === 'connected' ? 'Connecté' : 'Déconnecté'}
            icon={PhoneIcon}
            color={stats.whatsappStatus === 'connected' ? 'green' : 'red'}
            action={{
              label: stats.whatsappStatus === 'connected' ? 'Voir' : 'Connecter',
              onClick: () => window.location.href = '/app/settings'
            }}
          />
        </div>

        {/* Graphiques avec design amélioré */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {/* Graphique des revenus avec style premium */}
          <div className="lg:col-span-2 bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Revenus des 7 derniers jours</h3>
              <div className="p-2 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-lg">
                <ChartBarIcon className="h-4 w-4 text-white" />
              </div>
            </div>
            {revenueChartData && (
              <div className="h-64">
                <Line 
                  data={revenueChartData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        borderColor: '#2563eb',
                        borderWidth: 1,
                        cornerRadius: 8,
                        titleFont: { size: 12 },
                        bodyFont: { size: 11 },
                        callbacks: {
                          label: (context) => `${formatCurrency(context.parsed.y)} XOF`
                        }
                      }
                    },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: { color: '#6b7280', font: { size: 11 } }
                      },
                      y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(107, 114, 128, 0.08)' },
                        ticks: {
                          color: '#6b7280',
                          font: { size: 11 },
                          callback: (value) => `${formatCurrency(value)} XOF`
                        }
                      }
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Top produits avec style moderne */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Top Produits</h3>
              <div className="p-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-lg">
                <SparklesIcon className="h-4 w-4 text-white" />
              </div>
            </div>
            {topProductsData && (
              <div className="h-64">
                <Doughnut 
                  data={topProductsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: { 
                          boxWidth: 10, 
                          font: { size: 10 },
                          color: '#374151',
                          usePointStyle: true,
                          pointStyle: 'circle',
                          padding: 10
                        }
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        cornerRadius: 8,
                        titleFont: { size: 12 },
                        bodyFont: { size: 11 }
                      }
                    },
                    cutout: '65%'
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Commandes par jour et activité récente avec style amélioré */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {/* Graphique des commandes */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Commandes par jour</h3>
              <div className="p-2 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-lg">
                <ShoppingBagIcon className="h-4 w-4 text-white" />
              </div>
            </div>
            {ordersChartData && (
              <div className="h-64">
                <Bar 
                  data={ordersChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { 
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        cornerRadius: 8,
                        titleFont: { size: 12 },
                        bodyFont: { size: 11 }
                      }
                    },
                    scales: { 
                      x: {
                        grid: { display: false },
                        ticks: { color: '#6b7280', font: { size: 11 } }
                      },
                      y: { 
                        beginAtZero: true,
                        grid: { color: 'rgba(107, 114, 128, 0.08)' },
                        ticks: { color: '#6b7280', font: { size: 11 } }
                      }
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Activité récente modernisée */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">Activité récente</h3>
              <div className="p-2 bg-gradient-to-r from-green-400 to-green-500 rounded-lg">
                <ClockIcon className="h-4 w-4 text-white" />
              </div>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={activity.id} className="group flex items-center p-3 bg-gradient-to-r from-gray-50 to-blue-50/50 rounded-xl border border-gray-100/50 hover:border-blue-200/50 hover:shadow-sm transition-all duration-300">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-500 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300">
                        <ShoppingBagIcon className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-xs font-medium text-gray-900 group-hover:text-blue-900 transition-colors duration-300">{activity.title}</p>
                      <p className="text-xs text-gray-500">{activity.description}</p>
                    </div>
                    <div className="text-xs text-gray-400 bg-white/70 px-2 py-1 rounded-md">
                      {format(new Date(activity.time), 'dd/MM HH:mm', { locale: fr })}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <ClockIcon className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Aucune activité récente</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Raccourcis d'actions avec design premium */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Actions rapides</h3>
            <div className="p-2 bg-gradient-to-r from-orange-400 to-red-400 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickAction
              title="Nouvelle commande"
              description="Créer une commande"
              icon={PlusIcon}
              onClick={() => window.location.href = '/app/orders'}
              color="blue"
            />
            <QuickAction
              title="Gérer produits"
              description="Ajouter/modifier produits"
              icon={ShoppingBagIcon}
              onClick={() => window.location.href = '/app/products'}
              color="green"
            />
            <QuickAction
              title="Voir analytics"
              description="Statistiques détaillées"
              icon={ChartBarIcon}
              onClick={() => window.location.href = '/app/analytics'}
              color="purple"
            />

          </div>
        </div>
      </div>

      {/* CSS custom pour la scrollbar */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #1d4ed8);
          border-radius: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #2563eb, #1e40af);
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
